import React from "react";
import { Heart, MapPin, Share2, ArrowUpRight, ImageOff } from "lucide-react";
import { hasUsableImageUrl } from "../lib/placeholderImages";
import { normalizeLocationMapMedia } from "../lib/assetUrl";
import { formatLocationAddress, hasFormattedAddress } from "../lib/locationAddress";

/**
 * Thẻ địa điểm yêu thích (vintage) — dùng ở panel yêu thích trên header và trang cá nhân.
 */
export default function FavoritePlaceCard({ loc, onOpen, onShare, onRemove, onShowOnMap }) {
  const { image: cover, gallery: extras } = normalizeLocationMapMedia(loc);
  const thumb = hasUsableImageUrl(cover) ? cover : extras[0] || "";

  return (
    <article className="group overflow-hidden rounded-2xl border border-[#d9ccb4] bg-[#fffaf0] shadow-[0_6px_16px_rgba(80,65,40,0.07)] transition hover:-translate-y-0.5 hover:border-[#5f745f]/50 hover:shadow-[0_14px_28px_rgba(80,65,40,0.12)]">
      <div className="flex gap-3 p-2.5">
        <button
          type="button"
          onClick={() => onOpen?.(loc)}
          className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-[#e4d8c2] bg-[#efe6d4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5f745f]"
          aria-label={`Xem chi tiết ${loc.name}`}
        >
          {thumb ? (
            <img
              src={thumb}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-[#b5a78c]">
              <ImageOff size={20} />
            </span>
          )}
        </button>

        <div className="flex min-w-0 flex-1 flex-col">
          <button type="button" onClick={() => onOpen?.(loc)} className="min-w-0 text-left">
            <p className="line-clamp-2 font-faustina text-[15px] font-bold leading-snug text-[#2f352c] transition group-hover:text-[#3f5340]">
              {loc.name}
            </p>
          </button>

          {loc.categoryName ? (
            <span className="mt-1 inline-flex w-fit max-w-full items-center truncate rounded-full border border-[#d6dfcb] bg-[#eef2e6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3f5340]">
              {loc.categoryName}
            </span>
          ) : null}

          {hasFormattedAddress(loc) ? (
            <p className="mt-1 flex items-start gap-1 text-[11px] leading-snug text-[#7a7467]">
              <MapPin size={11} className="mt-px shrink-0" />
              <span className="line-clamp-1">{formatLocationAddress(loc)}</span>
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex items-center gap-1.5 border-t border-dashed border-[#e2d5bd] bg-[#f8f0df]/70 px-2.5 py-1.5">
        <button
          type="button"
          onClick={() => onOpen?.(loc)}
          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold text-[#4f6250] transition hover:bg-[#5f745f] hover:text-white"
        >
          Chi tiết
          <ArrowUpRight size={13} />
        </button>
        {onShowOnMap ? (
          <button
            type="button"
            onClick={() => onShowOnMap(loc)}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-[#7a7467] transition hover:bg-[#ece2cd] hover:text-[#2f352c]"
          >
            <MapPin size={13} />
            Bản đồ
          </button>
        ) : null}

        <div className="ml-auto flex items-center gap-1">
          {onShare ? (
            <button
              type="button"
              onClick={() => onShare(loc)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#3a6665] transition hover:bg-[#e3eeea]"
              title="Chia sẻ"
              aria-label="Chia sẻ"
            >
              <Share2 size={15} />
            </button>
          ) : null}
          {onRemove ? (
            <button
              type="button"
              onClick={() => onRemove(loc)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#a85d5d] transition hover:bg-[#f6e1de]"
              title="Bỏ yêu thích"
              aria-label="Bỏ yêu thích"
            >
              <Heart size={15} className="fill-current" />
            </button>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export function FavoriteEmptyState({ onExplore, compact = false }) {
  return (
    <div
      className={`rounded-2xl border border-dashed border-[#d3c4a6] bg-[#fffaf0] text-center ${
        compact ? "px-4 py-6" : "px-5 py-10"
      }`}
    >
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#f6e6e2] text-[#a85d5d]">
        <Heart size={22} />
      </div>
      <p className="mt-3 font-faustina text-base font-bold text-[#2f352c]">Chưa có địa điểm yêu thích</p>
      <p className="mx-auto mt-1 max-w-[260px] text-xs leading-relaxed text-[#7a7467]">
        Nhấn biểu tượng trái tim trên bản đồ hoặc trang địa điểm để lưu lại nơi bạn muốn ghé.
      </p>
      {onExplore ? (
        <button
          type="button"
          onClick={onExplore}
          className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#5f745f] px-4 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-[#4f6250]"
        >
          Khám phá bản đồ
          <ArrowUpRight size={14} />
        </button>
      ) : null}
    </div>
  );
}
